import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { contactInfo } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSubmitError('');
    try {
      await axios.post(`${BACKEND_URL}/api/contact/quote`, {
        ...formData,
        phone: formData.phone || null,
        company: formData.company || null,
        service: formData.service || null
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Unable to send your request. Please call us or try again.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-slate-700 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Get In Touch</h1>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto">
            Ready to optimize your supply chain? Let's talk about your logistics needs.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="p-6 border-slate-200 hover:shadow-xl transition-all duration-300">
                <div className="bg-slate-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Phone className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Phone</h3>
                <p className="text-slate-600">{contactInfo.phone}</p>
                <p className="text-sm text-slate-500 mt-2">Available 24/7/365</p>
              </Card>

              <Card className="p-6 border-slate-200 hover:shadow-xl transition-all duration-300">
                <div className="bg-slate-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
                <p className="text-slate-600 break-all">{contactInfo.email}</p>
                <p className="text-sm text-slate-500 mt-2">We'll respond within 24 hours</p>
              </Card>

              <Card className="p-6 border-slate-200 hover:shadow-xl transition-all duration-300">
                <div className="bg-slate-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Headquarters</h3>
                <p className="text-slate-600">{contactInfo.address}</p>
                <p className="text-sm text-slate-500 mt-2">Multiple FSL locations nationwide</p>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 border-slate-200 shadow-xl">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Request a Quote</h2>
                
                {submitted ? (
                  <div className="text-center py-12" data-testid="quote-success-message">
                    <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600">
                      We've received your inquiry and will contact you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                    {submitError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm" data-testid="quote-error-message">
                        {submitError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Full Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          data-testid="quote-name-input"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="border-slate-300 focus:ring-2 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          data-testid="quote-email-input"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="border-slate-300 focus:ring-2 focus:ring-slate-400"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          data-testid="quote-phone-input"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          className="border-slate-300 focus:ring-2 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Company Name
                        </label>
                        <Input
                          type="text"
                          name="company"
                          data-testid="quote-company-input"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="border-slate-300 focus:ring-2 focus:ring-slate-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Service Interested In
                      </label>
                      <select
                        name="service"
                          data-testid="quote-service-input"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-slate-900"
                      >
                        <option value="">Select a service</option>
                        <option value="fsl">Field Stocking Locations</option>
                        <option value="scm">Supply Chain Management</option>
                        <option value="warehousing">Warehousing & Fulfillment</option>
                        <option value="rush">Rush Delivery</option>
                        <option value="international">International Shipping</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                          data-testid="quote-message-input"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Tell us about your logistics needs..."
                        className="border-slate-300 focus:ring-2 focus:ring-slate-400 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={sending}
                      data-testid="quote-submit-btn"
                      className="w-full bg-gradient-to-r from-slate-800 to-slate-600 hover:from-slate-700 hover:to-slate-500 text-white py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Send className="mr-2 h-5 w-5" />
                      {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">24/7/365 Customer Support</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Our dedicated support team is available around the clock, every day of the year, to assist with your logistics needs. Whether it's tracking, quotes, or urgent shipments, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-lg">
              Call Now: {contactInfo.phone}
            </Button>
            <Button variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-3 rounded-lg">
              Email: {contactInfo.email}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;